import { useMemo } from 'react'

import { useUnit } from 'effector-react'
import { useNavigate, useParams } from 'react-router'

import EmptyIcon from '@/assets/images/not-found.svg'
import { Button } from '@/components/ui/button.tsx'
import { $creatorId } from '@/models/creators.ts'
import { ActionsRebillMessagesPage } from '@/modules/creator-settings/rebill-messages/components/create-rebill-messages-page.tsx'
import { EditRebillMessagesPageSkeleton } from '@/modules/creator-settings/rebill-messages/components/edit-rebill-messages-page-skeleton.tsx'
import { parseTimeStringToTimObject } from '@/modules/creator-settings/rebill-messages/helpers/time.ts'
import type { RebillMessage } from '@/modules/creator-settings/rebill-messages/types.ts'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { AppRoutes } from '@/utils/constants.ts'

export const EditRebillMessagePage = () => {
  const redirect = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { isFetching, rebillMessages } = useSettingsContext()
  const creatorId = useUnit($creatorId)

  const handleRedirect = () => {
    if (!creatorId) {
      return
    }
    redirect(AppRoutes.Creator.Settings.RebillMessages.asUrl(creatorId))
  }

  const message = useMemo(() => {
    const messageById = rebillMessages!.find((mes) => String(mes.id) === id)

    return {
      id: messageById?.id,
      messages: messageById?.sequence!.messages,
      includedFanLists: messageById?.includedFanLists,
      excludedFanLists: messageById?.excludedFanLists,
      timeframeStart: messageById?.timeframeStart
        ? parseTimeStringToTimObject(messageById?.timeframeStart)
        : null,
      timeframeEnd: messageById?.timeframeEnd
        ? parseTimeStringToTimObject(messageById?.timeframeEnd)
        : null,
    } as RebillMessage
  }, [rebillMessages, id])

  if (isFetching) {
    return <EditRebillMessagesPageSkeleton />
  }

  if (!message) {
    return (
      <div className="mt-8 flex flex-col items-center justify-center gap-3 text-center">
        <EmptyIcon />
        <p>You do not have any data</p>
        <Button onClick={handleRedirect}>Go to Rebill Messages</Button>
      </div>
    )
  }

  return (
    <div className="w-full p-8">
      <ActionsRebillMessagesPage
        data={message}
        isEdit
      />
    </div>
  )
}
